import { ErrorState, LoadingState } from '@buzzbee/ui';
import { useEffect } from 'react';
import { Shell } from './components/Shell';
import { DepositResultPage } from './components/rebooking-deposits';
import { matchPath, useRouter } from './lib/router';
import { useBrand, useMe } from './lib/session';
import { BookingPage } from './pages/book';
import { FrontDeskPage } from './pages/frontdesk';
import { OnboardingPage } from './pages/onboarding';
import { ForgotPasswordPage, PricingPage, ResetPasswordPage, SignInPage, SignUpPage, VerifyEmailPage } from './pages/public';
import { BillingPage, SettingsPage } from './pages/settings';
import { TestCallPage } from './pages/simulator';
import { BookingsPage, CallDetail, CallsPage, DashboardPage, InboxPage } from './pages/work';

const PUBLIC: Record<string, () => JSX.Element> = {
  '/pricing': PricingPage,
  '/sign-in': SignInPage,
  '/sign-up': SignUpPage,
  '/verify-email': VerifyEmailPage,
  '/forgot-password': ForgotPasswordPage,
  '/reset-password': ResetPasswordPage,
  // Where Stripe Checkout returns a customer after a deposit (no sign-in).
  '/deposit': DepositResultPage,
};

function Redirect({ to }: { to: string }) {
  const { navigate } = useRouter();
  useEffect(() => navigate(to, { replace: true }), [to, navigate]);
  return null;
}

function SignedIn() {
  const { path } = useRouter();
  const me = useMe();
  const ws = me.data!.current;
  useBrand(ws?.brand.product ?? null);
  if (!ws) return <ErrorState title="No workspace" error={new Error('Your account is not a member of any workspace yet.')} />;
  if (path === '/onboarding') return <div className="min-h-dvh bg-background px-4 py-10"><OnboardingPage /></div>;
  const call = matchPath('/calls/:id', path);
  let page: JSX.Element;
  if (path === '/') page = <DashboardPage />;
  else if (path === '/calls' || call) page = <><CallsPage />{call ? <CallDetail id={call.id!} /> : null}</>;
  else if (path === '/bookings') page = <BookingsPage />;
  else if (path === '/inbox') page = <InboxPage />;
  else if (path === '/test-call') page = <TestCallPage />;
  else if (path === '/front-desk') page = <FrontDeskPage />;
  else if (path === '/settings/billing') page = <BillingPage />;
  else if (path === '/settings') page = <SettingsPage />;
  else page = <ErrorState title="Page not found" error={new Error(path)} />;
  return <Shell>{page}</Shell>;
}

export function App() {
  const { path } = useRouter();
  const me = useMe();
  const Public = PUBLIC[path];
  if (Public) return <Public />;
  // The business's online booking page: public, never asks anyone to sign in.
  const book = matchPath('/book/:slug', path);
  if (book) return <BookingPage slug={book.slug!} />;
  if (me.isLoading) return <LoadingState className="min-h-dvh" />;
  if (me.error) return <ErrorState error={me.error} onRetry={() => me.refetch()} />;
  if (!me.data) return <Redirect to={path === '/' ? '/pricing' : '/sign-in'} />;
  return <SignedIn />;
}
